import React, { memo, useEffect, useState, useCallback, useMemo } from "react";
import Index from "../../../Index";
import PagesIndex from "../../../PagesIndex";
import { FreeMode, Autoplay, Navigation, EffectFade } from "swiper/modules";
import "swiper/css/effect-fade";
import {
  DataService,
  IMAGES_API_ENDPOINT,
} from "../../../../config/DataService";
import { Api } from "../../../../config/Api";

// Constants
const SWIPER_BREAKPOINTS = {
  550: { slidesPerView: 2.8, spaceBetween: 10 },
  780: { slidesPerView: 3, spaceBetween: 20 },
  1024: { slidesPerView: 4, spaceBetween: 20 },
  1349: { slidesPerView: 5, spaceBetween: 20 },
};

// Memoized movie slider component
const MovieSlider = memo(({ title, moviesList, isNowPlaying = false }) => {
  if (!moviesList?.length) return null;

  return (
    <Index.Box className="main-showing-part movie-slider">
      <Index.Box className="cus-container">
        <Index.Box className="showing-txt movie-slider-heading">
          <Index.Typography variant="h5">{title}</Index.Typography>
        </Index.Box>
        <Index.Box className="main-showing-slider movie-slider-inner">
          <PagesIndex.Swiper
            freeMode={true}
            modules={[Navigation, FreeMode]}
            navigation={true}
            spaceBetween={10}
            slidesPerView={2.5}
            breakpoints={SWIPER_BREAKPOINTS}
          >
            {moviesList.map((item, index) => (
              <PagesIndex.SwiperSlide key={item._id || index}>
                <PagesIndex.MovieCard
                  title={title}
                  item={item}
                  isNowPlaying={isNowPlaying}
                />
              </PagesIndex.SwiperSlide>
            ))}
          </PagesIndex.Swiper>
        </Index.Box>
      </Index.Box>
    </Index.Box>
  );
});

// Memoized banner slider component
const BannerSlider = memo(({ bannerList, updateBannerClickCount }) => {
  if (!bannerList?.length) return null;

  return (
    <Index.Box className="cus-container home-cus-container">
      <Index.Box className="account-ad-slider">
        <PagesIndex.Swiper
          modules={[Autoplay]}
          loop={true}
          speed={1500}
          autoplay={{ delay: 8000, disableOnInteraction: false }}
        >
          {bannerList.map((data, index) => (
            <PagesIndex.SwiperSlide key={data?._id || index}>
              <Index.Box
                className={`ad-slider-img ${data?.bannerLink ? "link" : ""}`}
                onClick={() => {
                  if (data?.bannerLink) {
                    window.open(data.bannerLink, "_blank");
                    updateBannerClickCount(data._id);
                  }
                }}
              >
                <img
                  src={data?.banner ? `${IMAGES_API_ENDPOINT}/${data.banner}` : PagesIndex.Png.NoImage}
                  alt="advertisement"
                />
              </Index.Box>
            </PagesIndex.SwiperSlide>
          ))}
        </PagesIndex.Swiper>
      </Index.Box>
    </Index.Box>
  );
});

function Home() {
  const dispatch = PagesIndex.useDispatch();
  const { region } = PagesIndex.useSelector((state) => state.UserReducer);


  const [state, setState] = useState({
    upcomingMoviesList: [],
    nowPlayingList: [],
    recentReleasesList: [],
    sliderList: [],
    aboveUpcomingBannerList: [],
    belowUpcomingBannerList: [],
    memberShipDetails: {},
  });

  const fetchData = useCallback(async (apiEndpoint) => {
    try {
      dispatch(PagesIndex.showLoader());
      const response = await DataService.get(apiEndpoint);
      return response?.data?.status === 200 ? response.data : null;
    } catch (error) {
      console.error("API Error:", error);
      return null;
    } finally {
      dispatch(PagesIndex.hideLoader());
    }
  }, [dispatch]);

  const updateBannerClickCount = useCallback(async (bannerId) => {
    try {
      await DataService.post(Api.UPDATE_BANNER_CLICK_COUNT, { bannerId });
    } catch (e) { console.error(e); }
  }, []);

  // Categorize upcoming movies using useMemo for performance
  const categorizedUpcoming = useMemo(() => {
    const categories = ["Bollywood", "Hollywood", "Regional"];
    return categories.map(cat => ({
      category: cat,
      movies: state.upcomingMoviesList.filter(m => m.movieCategory === cat)
    })).filter(group => group.movies.length > 0);
  }, [state.upcomingMoviesList]);

  // Combined fetch logic based on Region change
  useEffect(() => {
    const loadContent = async () => {
      if (!region?._id) return;

      const [
        sliderRes,
        nowPlayingRes,
        recentRes,
        bannerRes,
        memberRes,
        upcomingRes
      ] = await Promise.all([
        fetchData(`${Api.GET_SLIDER_IMAGES}/${region._id}/Web`),
        fetchData(`${Api.GET_MOVIES_BY_ID}/${region._id}`),
        fetchData(`${Api.GET_RECENT_RELEASES_MOVIE_BY_REGION}/${region._id}`),
        fetchData(`${Api.GET_BANNER_IMAGES}?bannerType=Web Banner`),
        fetchData(Api.GET_MEMBERSHIP_DETAILS),
        fetchData(`${Api.GET_UPCOMING_MOVIES}?regionId=${region._id}`)
      ]);

      setState(prev => ({
        ...prev,
        sliderList: sliderRes?.data || [],
        nowPlayingList: nowPlayingRes?.data || [],
        recentReleasesList: recentRes?.data?.recentReleasesMovies || [],
        memberShipDetails: memberRes?.data?.[0] || {},
        aboveUpcomingBannerList: bannerRes?.data?.filter(b => b.bannerShowSection?.includes("Above Upcoming Section")) || [],
        belowUpcomingBannerList: bannerRes?.data?.filter(b => b.bannerShowSection?.includes("Below Upcoming Section")) || [],
        upcomingMoviesList: upcomingRes?.data?.flatMap(item => 
          item.movies.filter(m => 
            PagesIndex.moment().utc().diff(PagesIndex.moment(m.filmOpeningDate), "months") <= 3
          )
        ) || []
      }));

      if (upcomingRes?.data) {
        dispatch(PagesIndex.upcomingMoviesList(upcomingRes.data));
      }
    };

    loadContent();
    window.scrollTo(0, 0);
  }, [region?._id, fetchData, dispatch]);

  return (
    <Index.Box className="main-home">
      {/* Hero Slider - Swapped react-slick for Swiper for consistency */}
      <Index.Box className="main-hero-slider">
        <PagesIndex.Swiper
          modules={[Autoplay, EffectFade]}
          effect="fade"
          loop={true}
          speed={1600}
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          className="hero-slick-details"
        >
          {state.sliderList.map((data, idx) => (
            <PagesIndex.SwiperSlide key={data._id || idx}>
              <Index.Box className="hero-slider-img">
                <img
                  src={data?.image ? `${IMAGES_API_ENDPOINT}/${data.image}` : PagesIndex.Png.NoImage}
                  alt="banner"
                  width="1920"
                  height="600"
                />
              </Index.Box>
            </PagesIndex.SwiperSlide>
          ))}
        </PagesIndex.Swiper>
      </Index.Box>

      <MovieSlider title="Now Showing" moviesList={state.nowPlayingList} isNowPlaying={true} />

      {/* Membership Section */}
      <Index.Box className="main-membership-plan">
        <Index.Box className="cus-container">
          <Index.Box className="membership-grid-main">
            <Index.Grid container>
              <Index.Grid item md={6} xxs={12}>
                {state.memberShipDetails?.file ? (
                  <video
                    className="membership-video"
                    autoPlay muted loop playsInline
                    id="membershipVideo"
                  >
                    <source
                      src={`${IMAGES_API_ENDPOINT}/${state.memberShipDetails.file}`}
                      type="video/mp4"
                    />
                  </video>
                ) : (
                  <img src={PagesIndex.Jpg.membership} className="member-img" alt="Membership" />
                )}
              </Index.Grid>
              <Index.Grid item md={6} xxs={12} className="membership-right-Index.Box">
                <Index.Box className="member-right-details">
                  <Index.Typography variant="h4">
                    {state.memberShipDetails?.title || "Introducing a loyalty program with special rewards!"}
                  </Index.Typography>
                  <Index.Box className="membership-btns main">
                    <Index.Link to="/membership" className="btn btn-primary">Purchase Plan</Index.Link>
                    <Index.Link to="https://ticketing.theconnplex.com/membership">Already Member?</Index.Link>
                  </Index.Box>
                  <Index.Box className="membership-btns store-btns">
                    <Index.Link to="https://play.google.com/store/apps/details?id=com.connplex" target="_blank">
                      <img src={PagesIndex.Svg.PlayStore} width="150" height="48" alt="Play Store" />
                    </Index.Link>
                    <Index.Link to="https://apps.apple.com/in/app/connplex-cinemas-tickets/id6497171599" target="_blank">
                      <img src={PagesIndex.Svg.AppleStore} width="150" height="48" alt="Apple Store" />
                    </Index.Link>
                  </Index.Box>
                </Index.Box>
              </Index.Grid>
            </Index.Grid>
          </Index.Box>
        </Index.Box>
      </Index.Box>

      <BannerSlider bannerList={state.aboveUpcomingBannerList} updateBannerClickCount={updateBannerClickCount} />

      {/* Categorized Upcoming Movies */}
      {categorizedUpcoming.map(group => (
        <MovieSlider key={group.category} title={`Upcoming ${group.category} Movies`} moviesList={group.movies} />
      ))}

      <MovieSlider title="Recent Releases" moviesList={state.recentReleasesList} isNowPlaying={true} />

      <BannerSlider bannerList={state.belowUpcomingBannerList} updateBannerClickCount={updateBannerClickCount} />

      <PagesIndex.Membership />
    </Index.Box>
  );
}

export default Home;